import React, { useState, useEffect, useRef } from "react";
import { ICompetencyMgmtPeriod } from "../../model/CompetencyMgmtPeriod.model";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import Loading from "../Loading/Loading";
import Modal from "../Modal/Modal";
import "./EditCompetencyMgmtPeriodComponent.scss";
import { toast } from 'react-toastify';
import CompetencyPeriodSelector from "../CompetencyPeriodSelector/CompetencyPeriodSelector";


const ADD_NEW = "add_new";

const EditCompetencyMgmtPeriodComponent: React.FC = () => {
    const [periods, setPeriods] = useState<ICompetencyMgmtPeriod[]>([]);
    const [selectedId, setSelectedId] = useState<string>("");
    const [periodSelectorKey, setPeriodSelectorKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState<ICompetencyMgmtPeriod | null>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiCFSkillPath.scopes.write
    });

    useEffect(() => {
        setLoading(true);
        execute("GET", "/api/CompetencyMgmtPeriod/getAll")
            .then((response: ICompetencyMgmtPeriod[]) => {
                const data = response ?? [];
                setPeriods(data);               
                
            })
            .catch((err: any) => {
                console.log("Error loading competency periods");
            })
            .finally(()=> setLoading(false));
    }, [execute]);

    const selectedPeriod = periods.find((p) => p.competencyMgmtPeriodId === selectedId) ?? null;
    const isAddNew = selectedId === ADD_NEW;
    // call this after a successful add or remove
    const refreshPeriods = () => setPeriodSelectorKey(k => k + 1);
   

    const handleAddNew = () => {
        setSelectedId(ADD_NEW);     
    };

    const validateDates = (startDate: string, endDate: string): boolean => {
        if (endDate <= startDate) {
            toast.error("End date must be after start date.");            
            return false;
        }
        return true;
    };

    const doUpdate = (updated: ICompetencyMgmtPeriod) => {
        setLoading(true);
        execute("PUT", "/api/CompetencyMgmtPeriod/update", updated)
            .then((response: ICompetencyMgmtPeriod) => {
                setPeriods((prev) =>
                    prev.map((p) => (p.competencyMgmtPeriodId === response.competencyMgmtPeriodId ? response : p))
                );
                refreshPeriods();
                toast.success('Period updated successfully.');
            })
            .catch((err: any) => {
                
                toast.error(err?.error ?? err?.message ?? "An error occurred.");
            })
            .finally(()=> setLoading(false));
    };

    const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedPeriod) return;
        const formData = new FormData(e.currentTarget);
        const updated: ICompetencyMgmtPeriod = {
            ...selectedPeriod,
            periodTitle: formData.get("periodTitle") as string,
            startDate: formData.get("startDate") as string,
            endDate: formData.get("endDate") as string,
        };
        if (!validateDates(updated.startDate, updated.endDate)) return;
        //Check if the title is being changed and the start date is changing and the start year is not this year
        //Should be creating new records for subsequent years so this is just a reminder if they try to change an existing one
        const titleChanged = updated.periodTitle !== selectedPeriod.periodTitle;
        const startDateChanged = updated.startDate !== selectedPeriod.startDate?.substring(0, 10);
        const startYear = parseInt(updated.startDate.substring(0, 4), 10);
        const startYearIsNotCurrentYear = startYear !== new Date().getFullYear() && startYear < new Date().getFullYear();

        if (titleChanged && startDateChanged && startYearIsNotCurrentYear) {
            setPendingUpdate(updated);
            setShowUpdateModal(true);
        } else {
            doUpdate(updated);
        }
    };

    const confirmUpdate = () => {
        if (!pendingUpdate) return;
        setShowUpdateModal(false);
        doUpdate(pendingUpdate);
    };

    const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const newPeriod = {
            periodTitle: formData.get("periodTitle") as string,
            startDate: formData.get("startDate") as string,
            endDate: formData.get("endDate") as string,
        };
        if (!validateDates(newPeriod.startDate, newPeriod.endDate)) return;
        setLoading(true);
        execute("POST", "/api/CompetencyMgmtPeriod/add", newPeriod)
            .then((response: ICompetencyMgmtPeriod) => {
                setPeriods((prev) => [...prev, response]);
                setSelectedId(response.competencyMgmtPeriodId);
                refreshPeriods();                
                toast.success('Period added successfully.');
            })
            .catch((err: any) => {                
                toast.error(err?.error ?? err?.message ?? "An error occurred.");
            })
            .finally(()=> setLoading(false));
    };

    const handleDelete = () => {
        if (!selectedPeriod) return;
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (!selectedPeriod) return;
        setShowDeleteModal(false);
        setLoading(true);
        execute("DELETE", `/api/CompetencyMgmtPeriod/delete/${selectedPeriod.competencyMgmtPeriodId}`)
            .then(() => {
                setPeriods((prev) => prev.filter((p) => p.competencyMgmtPeriodId !== selectedPeriod.competencyMgmtPeriodId));
                setSelectedId("");                
                refreshPeriods();
                toast.success('Period deleted successfully.');
            })
            .catch((err: any) => {                
                toast.error(err?.error ?? err?.message ?? "An error occurred.");
            })
            .finally(()=> setLoading(false));
    };

    return (
        <div className="max-width">
        <div className="cmp-period-container">
            <h2 className='h1-size'>Edit Competency Period</h2>
            <Loading isLoading={loading} />
            <Modal isOpen={showDeleteModal} modalTitle="Delete Competency Period" onClose={() => setShowDeleteModal(false)}>
                <p className="modal-form-content-scroll-container">Are you sure you want to delete <strong>{selectedPeriod?.periodTitle}</strong>?</p>
                <div className="modal-form-button-container two-button">
                    <button className="btn-med-gray" onClick={() => setShowDeleteModal(false)}>No</button>
                    <button className="btn-secondary" onClick={confirmDelete}>Yes</button>
                </div>
            </Modal>
            <Modal isOpen={showUpdateModal} modalTitle="Update Competency Period" onClose={() => setShowUpdateModal(false)}>
                <p className="modal-form-content-scroll-container">Are you sure you want to change <strong>{selectedPeriod?.periodTitle}</strong>? To create a new record, click the Add New button.</p>
                <div className="modal-form-button-container two-button">
                    <button className="btn-med-gray" onClick={() => { setShowUpdateModal(false); formRef.current?.reset(); }}>No</button>
                    <button className="btn-secondary" onClick={confirmUpdate}>Yes</button>
                </div>
            </Modal>

            {!isAddNew && (
            <div className="dis-flex gap-1 cmp-period-select-row">
                <CompetencyPeriodSelector key={periodSelectorKey} initialPeriodId={selectedId} onPeriodChange={(p) => { setSelectedId(p.competencyMgmtPeriodId); }} />
                    <button className="btn-primary" type="button" onClick={handleAddNew}>
                                Add New
                    </button>
            </div>
            )}

            {(selectedPeriod || isAddNew) && (
                <form ref={formRef} onSubmit={isAddNew ? handleAdd : handleUpdate} className="dis-flex dis-flex-col cmp-period-form m-t-1">
                    <p>Enter a title and select the start and end date for the competency management period. Managers will only be able to edit employee compentencies between these dates.</p>
                    <fieldset aria-labelledby='edit-skill-selector-heading' className='cmp-period-fieldset bg-white thick-border rounded'>
                        <legend className="on-admin clr-primary">
                            <h3 className='p-l--50 p-r--50 fnt-wght-700 ltr-spcng-normal m-b-0 m-t-0'>Edit Title and Dates</h3>
                        </legend>
                        <div className="dis-flex dis-flex-aln-center cmp-period-field-row">
                            <label htmlFor="periodTitle">Competency Period Title</label>
                            <input
                                id="periodTitle"
                                type="text"
                                name="periodTitle"
                                defaultValue={isAddNew ? "" : selectedPeriod?.periodTitle}
                                key={selectedId}
                                className="dis-flex-grow-1"
                                required
                                maxLength={150}
                            />
                        </div>
                        <div className="dis-flex dis-flex-aln-center cmp-period-field-row">
                            <label htmlFor="startDate">Competency Management Start Date</label>
                            <input
                                id="startDate"
                                type="date"
                                name="startDate"
                                defaultValue={isAddNew ? "" : selectedPeriod?.startDate?.substring(0, 10)}
                                key={selectedId + "_start"}
                                required                                
                            />
                        </div>
                        <div className="dis-flex dis-flex-aln-center cmp-period-field-row">
                            <label htmlFor="endDate">Competency Management End Date</label>
                            <input
                                id="endDate"
                                type="date"
                                name="endDate"
                                defaultValue={isAddNew ? "" : selectedPeriod?.endDate?.substring(0, 10)}
                                key={selectedId + "_end"}
                                required
                            />
                        </div>
                        <div className="dis-flex cmp-period-actions">
                            <button className="btn-outlined-primary" type="submit">
                                {isAddNew ? "Add" : "Update"}
                            </button>
                            {isAddNew && (
                                <button className="btn-outlined-primary" type="button" onClick={() => {setSelectedId(""); refreshPeriods();}}>
                                    Cancel
                                </button>
                            )}
                            {!isAddNew && (
                                <button className="btn-outlined-primary" type="button" onClick={handleDelete}>
                                    Delete
                                </button>
                            )}
                        </div>
                    </fieldset>
                </form>
            )}
        </div>
        </div>
    );
};

export default EditCompetencyMgmtPeriodComponent;
